import React, { useState, useCallback, useEffect } from 'react';
import PropTypes, { any } from 'prop-types';
import moment from 'moment';

import SearchableMultiSelectPopOverMenu from '../../../../../components/SearchableMultiSelectPopOverMenu';

import { debounce } from '../../../../../utils/debounceUtil';
import DebouncedFilterInput from '../../../../../components/FormElementsStandalone/DebouncedFilterInput';

import styles from './preRegInvitesTabContent.scss';

const getMultiSelectMenuOptions = (preRegFilterOptions, dataKey, displayString = '', isDateType = false) => {
    let options = preRegFilterOptions?.[dataKey] ?? [];

    if (isDateType) {
        options = options.map((option) => ({
            ...option,
            label: moment(option?.label).format('MMM DD, YYYY'),
        }));
    }

    return [
        { id: '', value: '', label: `All ${displayString}`, onlySingleSelect: true },
        ...options,
    ];
};

const getSelectedFilterLabel = (selectedIds, options, displayString) => {
    if (selectedIds?.length > 0) {
        const selectedOptions = options.filter((option) => selectedIds.includes(option.id));

        if (selectedOptions.length === 1) {
            return selectedOptions[0].label;
        }

        if (selectedOptions.length > 1) {
            return `Multiple ${displayString}`;
        }
    }

    return `All ${displayString}`;
};

const PreRegInvitesFilters = (props) => {
    const {
        onUpdateFilters,
        preRegFilterOptions,
    } = props;

    const [filters, setFilters] = useState({
        search: '',
        divisionCategoryIds: [],
        medalTiers: [],
        statusIds: [],
        sentDates: [],
        expirationDates: [],
    });

    useEffect(() => {
        onUpdateFilters(filters);
    }, [filters]);

    const onResetClick = useCallback(() => {
        setFilters({
            ...filters,
            divisionCategoryIds: [],
            medalTiers: [],
            statusIds: [],
            sentDates: [],
            expirationDates: [],
        });
    }, [filters]);

    const categoryOptions = getMultiSelectMenuOptions(preRegFilterOptions, 'divisionCategories', 'categories', false);
    const statusOptions = getMultiSelectMenuOptions(preRegFilterOptions, 'statuses', 'statuses', false);
    const medalTierOptions = getMultiSelectMenuOptions(preRegFilterOptions, 'medalTiers', 'medals', false);
    const sentDateOptions = getMultiSelectMenuOptions(preRegFilterOptions, 'sentDates', 'sent dates', true);
    const expirationDateOptions = getMultiSelectMenuOptions(preRegFilterOptions, 'expirationDates', 'expiration dates', true);

    const handleMultiSelectOptionChange = useCallback((key) => (selectedOptions) => {
        const updatedFilters = {
            ...filters,
            [key]: selectedOptions?.map((option) => option?.id).filter((id) => !!id),
        };
        setFilters(updatedFilters);
    }, [filters]);

    const handleSearch = useCallback((searchTerm) => {
        const updatedFilters = {
            ...filters,
            search: searchTerm,
        };
        setFilters(updatedFilters);
    }, [filters]);

    const debouncedHandleSearch = debounce(handleSearch, 250);

    const shouldShowResetFilters = filters.divisionCategoryIds.length > 0 ||
        filters.medalTiers.length > 0 ||
        filters.statusIds.length > 0 ||
        filters.sentDates.length > 0 ||
        filters.expirationDates.length > 0;

    return (
        <div className={`d-flex ${styles.filtersWrapper}`}>
            <div className={styles.anchorWrapper}>
                <SearchableMultiSelectPopOverMenu
                    menuItems={categoryOptions}
                    onSelected={handleMultiSelectOptionChange('divisionCategoryIds')}
                    selectedValues={filters.divisionCategoryIds}
                    alignLeft={true}
                    attributeToSave="id"
                    attributeToShow="label"
                    isSearchable={false}
                >
                    <div className={`${styles.anchorClass}`}>
                        <div className={`ellipsis`}>
                            {getSelectedFilterLabel(filters.divisionCategoryIds, categoryOptions, 'categories')}
                        </div>
                        <i className={`${styles.arrow} uts uts-expand`} />
                    </div>
                </SearchableMultiSelectPopOverMenu>
            </div>
            <div className={styles.anchorWrapper}>
                <SearchableMultiSelectPopOverMenu
                    menuItems={statusOptions}
                    onSelected={handleMultiSelectOptionChange('statusIds')}
                    selectedValues={filters.statusIds}
                    alignLeft={true}
                    attributeToSave="id"
                    attributeToShow="label"
                    isSearchable={false}
                >
                    <div className={`${styles.anchorClass}`}>
                        <div className={`ellipsis`}>
                            {getSelectedFilterLabel(filters.statusIds, statusOptions, 'statuses')}
                        </div>
                        <i className={`${styles.arrow} uts uts-expand`} />
                    </div>
                </SearchableMultiSelectPopOverMenu>
            </div>
            <div className={styles.anchorWrapper}>
                <SearchableMultiSelectPopOverMenu
                    menuItems={medalTierOptions}
                    onSelected={handleMultiSelectOptionChange('medalTiers')}
                    selectedValues={filters.medalTiers}
                    alignLeft={true}
                    attributeToSave="id"
                    attributeToShow="label"
                    isSearchable={false}
                >
                    <div className={`${styles.anchorClass}`}>
                        <div className={`ellipsis`}>
                            {getSelectedFilterLabel(filters.medalTiers, medalTierOptions, 'medals')}
                        </div>
                        <i className={`${styles.arrow} uts uts-expand`} />
                    </div>
                </SearchableMultiSelectPopOverMenu>
            </div>
            <div className={styles.anchorWrapper}>
                <SearchableMultiSelectPopOverMenu
                    menuItems={sentDateOptions}
                    onSelected={handleMultiSelectOptionChange('sentDates')}
                    selectedValues={filters.sentDates}
                    alignLeft={true}
                    attributeToSave="id"
                    attributeToShow="label"
                    isSearchable={false}
                >
                    <div className={`${styles.anchorClass}`}>
                        <div className={`ellipsis`}>
                            {getSelectedFilterLabel(filters.sentDates, sentDateOptions, 'sent dates')}
                        </div>
                        <i className={`${styles.arrow} uts uts-expand`} />
                    </div>
                </SearchableMultiSelectPopOverMenu>
            </div>
            <div className={styles.anchorWrapper}>
                <SearchableMultiSelectPopOverMenu
                    menuItems={expirationDateOptions}
                    onSelected={handleMultiSelectOptionChange('expirationDates')}
                    selectedValues={filters.expirationDates}
                    alignLeft={true}
                    attributeToSave="id"
                    attributeToShow="label"
                    isSearchable={false}
                >
                    <div className={`${styles.anchorClass}`}>
                        <div className={`ellipsis`}>
                            {getSelectedFilterLabel(filters.expirationDates, expirationDateOptions, 'expiration dates')}
                        </div>
                        <i className={`${styles.arrow} uts uts-expand`} />
                    </div>
                </SearchableMultiSelectPopOverMenu>
            </div>
            {shouldShowResetFilters && (
                <div
                    className={`${styles.filter} clickable link fs14 d-flex aic`}
                    onClick={onResetClick}
                >
                    Reset
                </div>
            )}
            <div className={`${styles.anchorWrapper} ${styles.searchWrapper}`}>
                <DebouncedFilterInput
                    className={`${styles.anchorClass} ${styles.searchInput}`}
                    placeholder="Find Player(s)"
                    hideSearchIcon
                    value={filters.searchTerm}
                    onChange={debouncedHandleSearch}
                />
            </div>
        </div>
    );
};

PreRegInvitesFilters.propTypes = {
    onUpdateFilters: PropTypes.func.isRequired,
    preRegFilterOptions: PropTypes.objectOf(any).isRequired,
};

PreRegInvitesFilters.defaultProps = {
};

export default PreRegInvitesFilters;
